import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import isNil from 'lodash/fp/isNil';
import { useMeContext } from 'modules/me';

export type FeatureFlagKeys =
  | 'isMenuFeedbackEnabled'
  | 'isTiWebNotesInsightsPageEnabled'
  | 'isTiWebTabPersistedCacheQueryEnabled'
  | 'isTiAutoAcceptUserSettingsEnabled'
  | 'isShowCollapseAll'
  | 'isTIWebGroupViewEnabled'
  | 'isBulkEditPerformanceEnabled'
  | 'isTiwDurationIndependentStartEndTime'
  | 'isSmartTimesheetPerformanceEnabled'
  | 'isOfflineImprovementsEnabled'
  | 'isTiwUnifiedCollectorsTab'
  | 'isMyWorkPerformanceEnabled'
  | 'isSmartDiaryBetterUxEnabled'
  | 'isSessionV2Enabled'
  | 'isTiwNewLoginDesign'
  | 'isTiwPauseTimesignalPollEnabled'
  | 'isTiwSupervisorInsightsEnabled';

export const FeatureFlagKeyMap = new Map<string, string>([
  ['isTiwNewLoginDesign', 'tiwNewLoginDesign']
]);

export type FeatureFlagType = boolean | string | number;
export function useFeatureFlag<FeatureFlagType = boolean>(
  key: FeatureFlagKeys,
  defaultValue?: FeatureFlagType
): FeatureFlagType {
  const flags = useFlags();
  const ldFlagKey = FeatureFlagKeyMap.get(key);
  const userlessFlag = ldFlagKey ? flags[ldFlagKey] : undefined;
  const me = useMeContext();
  return useMemo(() => {
    if (isNil(me) && userlessFlag !== undefined) {
      return userlessFlag;
    }
    return (me?.featureFlags[key] ?? defaultValue) as FeatureFlagType;
  }, [defaultValue, key, me, userlessFlag]);
}

export const useFeatureFlags = (
  keys: FeatureFlagKeys[],
  defaultValue = false
): Record<FeatureFlagKeys, boolean> => {
  const flags = useFlags();

  const me = useMeContext();
  return useMemo(
    () =>
      keys.reduce((acc, key) => {
        const ldFlagKey = FeatureFlagKeyMap.get(key);
        const userlessFlag = ldFlagKey ? flags[ldFlagKey] : undefined;

        let resolvedFlag = me?.featureFlags[key];

        if (me === null && userlessFlag !== undefined && userlessFlag) {
          resolvedFlag = userlessFlag;
        }

        return {
          ...acc,
          [key]: resolvedFlag ?? defaultValue
        };
      }, {} as Record<FeatureFlagKeys, boolean>),
    [keys, me, defaultValue, flags]
  );
};
